import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { apiClient, ApiError } from "@/api/client";
import type { ConsentRecord } from "@/api/types";
import {
  beginAnalyticsConsent,
  clearAnalyticsIdentifiers,
  setAnalyticsConsent,
} from "@/lib/analytics/statsig";
import {
  forgetAnalyticsChoice,
  readAnalyticsChoice,
  recordAnalyticsChoice,
  type AnalyticsChoice,
} from "@/lib/consent/analyticsChoice";
import {
  CONSENT_PURPOSES,
  POLICY_VERSION,
  PRODUCT_ANALYTICS,
  type ConsentPurpose,
} from "@/lib/consent/policy";
import { useAuthStore } from "@/stores/auth";

/**
 * Per-purpose consent state, and the only thing that turns analytics on
 * (contract A8/A25, PRD §9.1.1, DPIA P6).
 *
 * **Two sources, one gate.** For a signed-in user the answer is the
 * server's `consents` rows; for a visitor without an account it is the
 * choice remembered on this device (`lib/consent/analyticsChoice.ts`).
 * Either way it reaches Statsig only through `setAnalyticsConsent`.
 *
 * **One person at a time.** `refresh` notices when the identity on screen
 * has changed — sign-in, sign-out, a different account — and before
 * anything else drops the previous answer, stops the analytics client and
 * clears the device's analytics identifiers. Analytics stays off until the
 * new identity's own answer is known, and a fetch that lands for an
 * identity no longer on screen is discarded.
 *
 * **Fail closed.** `live` is false for every purpose until a fetch proves
 * otherwise, and a failed fetch resets it to false rather than leaving the
 * last known answer in place. An unreadable consent state is treated as no
 * consent: the cost is a missing analytics event, and the alternative cost
 * is processing personal data without a lawful basis.
 *
 * **Liveness is computed, never stored as a flag.** `GET /me/consents`
 * returns rows for every policy version, superseded ones included, so a row
 * counts only when `withdrawn_at === null` *and* its `policy_version` is
 * the one this build renders. New wording therefore silently re-asks
 * instead of inheriting an answer given to different wording.
 *
 * **Registration writes nothing here.** §9.1.1/A25: registration completes
 * on the contract basis with no consent row, and no consent is a condition
 * of registering. A row appears only when a signed-in user accepts in the
 * analytics banner or ticks a box on the Account page.
 */
export const useConsentStore = defineStore("consent", () => {
  const auth = useAuthStore();

  const records = ref<readonly ConsentRecord[]>([]);
  const loading = ref(false);
  const saving = ref<ConsentPurpose | null>(null);
  const error = ref<string | null>(null);
  // Set when the server rejects our `policy_version`: this build renders
  // wording the server has superseded, so the honest fix is to reload into
  // the build that carries the new wording, not to retry the same body.
  const policyOutdated = ref(false);

  // Whose answer the state here holds: a user id, null for a visitor
  // without an account, undefined before the first `refresh`.
  const identity = ref<string | null | undefined>(undefined);
  // Whether the answer for `identity` is known. A failed fetch leaves it
  // false: an unknown answer must neither turn analytics on nor re-ask
  // someone who may already have answered.
  const resolved = ref(false);
  // What this device remembers for `identity`; see `analyticsChoice.ts`.
  const deviceChoice = ref<AnalyticsChoice | null>(null);
  // The footer's "Analytics settings" asked for the banner back.
  const choiceReopened = ref(false);
  // Bumped by every `refresh`; a refresh that finds it has moved on lost a
  // race with a newer one and must not apply its result.
  let generation = 0;

  const live = computed<Record<ConsentPurpose, boolean>>(() => {
    const result = {} as Record<ConsentPurpose, boolean>;
    for (const purpose of CONSENT_PURPOSES) {
      result[purpose] = records.value.some(
        (row) =>
          row.purpose === purpose &&
          row.withdrawn_at === null &&
          row.policy_version === POLICY_VERSION,
      );
    }
    return result;
  });

  /** Whether analytics is on for the person on screen. */
  const analyticsOn = computed(() =>
    identity.value === null
      ? deviceChoice.value === "accepted"
      : live.value[PRODUCT_ANALYTICS],
  );

  /**
   * Whether the analytics banner is showing.
   *
   * It shows on request, and otherwise to anyone whose answer is known to
   * be missing at this policy version: a visitor whose device holds no
   * choice, or a signed-in user with no live row and no refusal remembered
   * on this device for that account.
   */
  const analyticsChoiceOpen = computed(() => {
    if (choiceReopened.value) return true;
    if (!resolved.value) return false;
    if (identity.value === null) return deviceChoice.value === null;
    return !live.value[PRODUCT_ANALYTICS] && deviceChoice.value !== "rejected";
  });

  /**
   * Resolve consent for whoever is signed in now, and apply it to the
   * analytics client. Called at boot, on every identity change, when a
   * sign-up's account has been created, and after every change of answer.
   */
  async function refresh(): Promise<void> {
    const next = auth.user?.id ?? null;
    const run = ++generation;

    if (next !== identity.value) {
      const switching = identity.value !== undefined;
      identity.value = next;
      records.value = [];
      deviceChoice.value = null;
      resolved.value = false;
      choiceReopened.value = false;
      error.value = null;
      policyOutdated.value = false;
      // Detaches the previous person's client and opens the new identity's
      // event queue before this function yields to anything else.
      await beginAnalyticsConsent(next);
      if (switching) clearAnalyticsIdentifiers();
      if (run !== generation) return;
    }

    if (next === null) {
      loading.value = false;
      deviceChoice.value = readAnalyticsChoice(null);
      resolved.value = true;
      await setAnalyticsConsent(deviceChoice.value === "accepted", null);
      return;
    }

    // A sign-up whose account does not exist yet: the previous identity is
    // already detached above and this one's events are held, but the API
    // would refuse the fetch. It runs when the account is created.
    if (auth.accountPending) return;

    loading.value = true;
    error.value = null;
    let rows: readonly ConsentRecord[] = [];
    let failure: string | null = null;
    try {
      rows = await apiClient.getConsents();
    } catch (e) {
      failure =
        e instanceof ApiError
          ? `API error ${e.status}`
          : "Could not load your privacy choices";
    }
    if (run !== generation) return;
    loading.value = false;
    records.value = rows;
    error.value = failure;
    deviceChoice.value = readAnalyticsChoice(next);
    resolved.value = failure === null;
    await setAnalyticsConsent(live.value[PRODUCT_ANALYTICS], next);
  }

  /**
   * Give or withdraw one consent for the signed-in user. The same call in
   * both directions — A25 requires withdrawal to be as easy as the opt-in,
   * so there is one function, one request and no confirmation step either
   * way.
   */
  async function setConsent(
    purpose: ConsentPurpose,
    granted: boolean,
  ): Promise<void> {
    const owner = auth.user?.id ?? null;
    saving.value = purpose;
    error.value = null;
    policyOutdated.value = false;
    try {
      if (granted) {
        await apiClient.giveConsent(purpose, POLICY_VERSION);
      } else {
        await apiClient.withdrawConsent(purpose);
        // Stop the vendor before re-reading state, not after. Teardown is
        // the safe direction: if the refresh below fails, analytics is
        // already off rather than still running on a consent the user has
        // just withdrawn.
        if (purpose === PRODUCT_ANALYTICS) {
          await setAnalyticsConsent(false);
          clearAnalyticsIdentifiers();
        }
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        policyOutdated.value = true;
        error.value =
          "Our privacy wording has changed since this page loaded. Reload, then choose again.";
      } else {
        error.value = granted
          ? "Could not save that choice. Nothing has been turned on."
          : "Could not withdraw that consent. Please try again.";
      }
      // The toggle reads from `records`, which this failure left untouched,
      // so the control snaps back to the server's answer on its own.
      return;
    } finally {
      saving.value = null;
    }
    if (purpose === PRODUCT_ANALYTICS && owner !== null) {
      // The server holds the consent itself; the device only remembers a
      // refusal, so the banner does not ask again after a withdrawal.
      if (granted) forgetAnalyticsChoice(owner);
      else recordAnalyticsChoice(owner, "rejected");
    }
    await refresh();
  }

  /**
   * The banner's two buttons: Accept analytics and Reject, equal in every
   * respect including what it takes to undo them.
   *
   * Without an account the answer is recorded on this device and applied
   * straight away. Signed in, Accept writes a consent row; Reject withdraws
   * a live one, or, if there is none, remembers the refusal on this device
   * for this account. Reject always leaves no analytics identifier behind.
   */
  async function chooseAnalytics(granted: boolean): Promise<void> {
    const owner = auth.user?.id ?? null;
    if (owner === null) {
      const choice: AnalyticsChoice = granted ? "accepted" : "rejected";
      recordAnalyticsChoice(null, choice);
      deviceChoice.value = choice;
      choiceReopened.value = false;
      await setAnalyticsConsent(granted, null);
      if (!granted) clearAnalyticsIdentifiers();
      return;
    }
    if (granted !== live.value[PRODUCT_ANALYTICS]) {
      await setConsent(PRODUCT_ANALYTICS, granted);
      // A failed save keeps the banner up with the error showing.
      if (error.value !== null) return;
    } else if (!granted) {
      recordAnalyticsChoice(owner, "rejected");
      deviceChoice.value = "rejected";
      clearAnalyticsIdentifiers();
    }
    choiceReopened.value = false;
  }

  /** Bring the banner back so the answer can be changed. */
  function openAnalyticsChoice(): void {
    choiceReopened.value = true;
  }

  // A sign-up's account now exists: resolve the consent `refresh` deferred.
  watch(
    () => auth.accountPending,
    (pending, wasPending) => {
      if (wasPending && !pending) void refresh();
    },
  );

  return {
    records,
    live,
    loading,
    saving,
    error,
    policyOutdated,
    analyticsOn,
    analyticsChoiceOpen,
    analyticsChoiceReopened: choiceReopened,
    refresh,
    setConsent,
    chooseAnalytics,
    openAnalyticsChoice,
  };
});
